import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Share2,
  Check,
  Sparkles,
  ExternalLink,
  ChevronRight,
  CheckSquare,
  Square,
  Zap,
  Bot,
  Search,
  Layers,
  ArrowUpRight,
  ShieldCheck,
  AlertCircle,
  Table,
  BarChart3,
  FileSpreadsheet,
  Activity,
  Compass
} from "lucide-react";

export default function MeasureAiVisibilityPostPage() {
  const [copied, setCopied] = useState(false);
  const [checkedSteps, setCheckedSteps] = useState({});

  const toggleStep = (id) => {
    setCheckedSteps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const originalTitle = document.title;
    const pageTitle = "How to Measure AI Search Visibility for Free (Without Paid Tools) | Kanna Narendra Kumar";
    const pageDesc = "Learn how to measure AI search performance for free using Google Search Console, Bing Webmaster Tools, server logs, GA4, and Microsoft Clarity. Complete step-by-step framework by Kanna Narendra Kumar.";
    const pageUrl = "https://narendradigital.in/blog/how-to-measure-ai-search-visibility-for-free/";

    document.title = pageTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    setMeta('meta[name="description"]', "content", pageDesc);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDesc);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDesc);
    setMeta('meta[name="twitter:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    canonical.setAttribute("href", pageUrl);

    // Inject JSON-LD Schema: TechArticle + HowTo + BreadcrumbList + FAQPage
    const scriptId = "measure-ai-visibility-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            "@id": `${pageUrl}#article`,
            "isPartOf": {
              "@type": "WebSite",
              "@id": "https://narendradigital.in/#website",
              "name": "Kanna Narendra Kumar — Senior SEO & AI Search Strategist",
              "url": "https://narendradigital.in/"
            },
            "headline": "How to Measure AI Search Visibility for Free (Without Paid Tools)",
            "description": pageDesc,
            "image": "https://narendradigital.in/og-image-v3.jpg",
            "datePublished": "2026-10-14T10:00:00+05:30",
            "dateModified": "2026-10-14T18:00:00+05:30",
            "mainEntityOfPage": pageUrl,
            "author": {
              "@type": "Person",
              "@id": "https://narendradigital.in/#author",
              "name": "Kanna Narendra Kumar",
              "jobTitle": "Senior SEO Specialist & AI Search Strategist",
              "url": "https://narendradigital.in/",
              "sameAs": [
                "https://www.linkedin.com/in/narendra-kumar-kanna/"
              ]
            },
            "publisher": {
              "@type": "Person",
              "name": "Kanna Narendra Kumar"
            },
            "keywords": [
              "How to Measure AI Search Visibility for Free",
              "Measure AI Search Performance Without Paid Tools",
              "Google Search Console AI Performance",
              "Bing Webmaster Tools AI Performance Citations",
              "Server Log Analysis for AI Crawlers",
              "OAI-SearchBot GPTBot Tracking",
              "GA4 AI Referral Traffic Acquisition",
              "Microsoft Clarity AI Visitor Behaviour",
              "AI Search Tracking Spreadsheet Template",
              "Free AI SEO Analytics Framework"
            ],
            "articleSection": "AEO/GEO , AI Visibility",
            "inLanguage": "en-US"
          },
          {
            "@type": "HowTo",
            "@id": `${pageUrl}#howto`,
            "name": "How to Measure AI Search Visibility for Free (Without Paid Tools)",
            "description": "A comprehensive 9-step guide to measuring website AI search performance, crawler requests, citations, and referral traffic using free tools.",
            "totalTime": "PT30M",
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Select Your Important Pages",
                "text": "Start with product/service pages, comparison pages, high-intent landing pages, and business blogs rather than tracking every URL."
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Collect Google Search Console Data",
                "text": "Open Google Search Console, access the Generative AI performance report, and export AI impressions organized by page and week."
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Collect Bing Webmaster Tools Data",
                "text": "Access the AI Performance report in Bing Webmaster Tools and record weekly citation counts for your selected pages."
              },
              {
                "@type": "HowToStep",
                "position": 4,
                "name": "Analyze Server Log Files",
                "text": "Identify AI search crawlers like OAI-SearchBot in your server logs, separating search bots from training bots (GPTBot) and user fetches."
              },
              {
                "@type": "HowToStep",
                "position": 5,
                "name": "Collect AI Referral Traffic From GA4",
                "text": "Navigate to Traffic acquisition in GA4 and filter for referral sessions, engaged sessions, and conversions originating from ChatGPT and Claude."
              },
              {
                "@type": "HowToStep",
                "position": 6,
                "name": "Use Microsoft Clarity to Understand Visitor Behaviour",
                "text": "Analyze scroll depth, heatmaps, and form interactions for visitors arriving through identifiable AI referrals."
              },
              {
                "@type": "HowToStep",
                "position": 7,
                "name": "Combine the Data in One Spreadsheet",
                "text": "Organize Page URL, Week, AI bot requests, Google AI impressions, Bing citations, and GA4 AI referral sessions in a weekly tracking sheet."
              },
              {
                "@type": "HowToStep",
                "position": 8,
                "name": "Understand What the Data Is Telling You",
                "text": "Interpret the signals: Crawled (Logs) → Visible/Cited (GSC/Bing) → Clicked (GA4) → Visited (Clarity), and apply the 4-quadrant diagnostic matrix."
              },
              {
                "@type": "HowToStep",
                "position": 9,
                "name": "Track Performance for Three Months",
                "text": "Repeat weekly for 3 months, correlating content and technical updates with changes in AI crawler activity, impressions, citations, and conversions."
              }
            ]
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://narendradigital.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://narendradigital.in/blog/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "How to Measure AI Search Visibility for Free",
                "item": pageUrl
              }
            ]
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How can I measure AI search visibility for free without paid tools?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can measure AI search performance completely free using five accessible data sources: Google Search Console (AI Performance), Bing Webmaster Tools (AI Citations), Server Log Files (AI bot requests), Google Analytics 4 (AI referral traffic), and Microsoft Clarity (user session behaviour)."
                }
              },
              {
                "@type": "Question",
                "name": "What is the difference between Google AI impressions and Bing citations?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Google AI impressions record how often your page was displayed within Google's generative search experiences (such as AI Overviews), whereas Bing Webmaster Tools measures direct citations where your URL was linked as a referenced source in Microsoft Copilot and AI-powered Bing answers. They represent different surfaces and should never be merged into one aggregate score."
                }
              },
              {
                "@type": "Question",
                "name": "What is the difference between OAI-SearchBot, GPTBot, and ChatGPT-User in server logs?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "OAI-SearchBot is OpenAI's dedicated crawler designed to discover and retrieve web content for ChatGPT search results. GPTBot crawls web pages for potential foundation model training. ChatGPT-User executes real-time fetches directly triggered by a user prompt in ChatGPT. For AI search visibility tracking, focus strictly on search and retrieval crawlers like OAI-SearchBot."
                }
              },
              {
                "@type": "Question",
                "name": "Does an AI bot requesting my page mean it was cited?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. A bot request in server logs simply confirms that an AI crawler fetched the page. It does not mean the page was understood, indexed, retrieved, cited, or shown to an end user."
                }
              },
              {
                "@type": "Question",
                "name": "What should I do if AI crawler activity is high but reported AI visibility is low?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "If AI bots are frequently crawling your pages but reported citations or impressions remain low, investigate whether your content directly answers user questions with atomic clarity, provides original data or unique perspectives, or if critical text is locked behind client-side JavaScript rendering."
                }
              }
            ]
          }
        ]
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stepsTrackingList = [
    {
      id: "s1",
      stepNum: "01",
      title: "Select Important URLs",
      desc: "Define a fixed tracking basket of high-intent service, product, comparison, and core business pages."
    },
    {
      id: "s2",
      stepNum: "02",
      title: "GSC Generative AI Export",
      desc: "Pull weekly AI impressions by URL from Google Search Console's AI performance report."
    },
    {
      id: "s3",
      stepNum: "03",
      title: "Bing AI Citations Export",
      desc: "Record weekly citations in Bing Webmaster Tools Copilot and AI reports."
    },
    {
      id: "s4",
      stepNum: "04",
      title: "Server Log Filtering",
      desc: "Isolate search/retrieval crawlers (OAI-SearchBot) from training bots (GPTBot) and count weekly 200 OK hits."
    },
    {
      id: "s5",
      stepNum: "05",
      title: "GA4 AI Referral Tracking",
      desc: "Record sessions, engagement rate, and goal conversions from ChatGPT, Claude, and AI platforms."
    },
    {
      id: "s6",
      stepNum: "06",
      title: "Clarity Behavior Analysis",
      desc: "Inspect scroll depth, session recordings, and form interaction for AI referral cohorts."
    },
    {
      id: "s7",
      stepNum: "07",
      title: "Consolidate Master Sheet",
      desc: "Tabulate all 5 streams into weekly columns without aggregating incompatible metrics."
    },
    {
      id: "s8",
      stepNum: "08",
      title: "Signal Matrix Diagnosis",
      desc: "Diagnose crawl vs visibility mismatches using the 4-quadrant action matrix."
    },
    {
      id: "s9",
      stepNum: "09",
      title: "90-Day Trend Review",
      desc: "Correlate technical/content releases against 12-week citation and traffic trajectories."
    }
  ];

  const completedStepsCount = Object.values(checkedSteps).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#070a12] text-white selection:bg-cyan-500 selection:text-black">
      {/* Background Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-indigo-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed top-96 right-10 w-[500px] h-[350px] bg-cyan-600/10 rounded-full blur-[160px] pointer-events-none" />

      {/* Main Global Navigation Bar */}
      <Navbar />

      <main className="max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 relative z-10">
        {/* Article Breadcrumbs & Quick Utility Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <nav className="flex items-center gap-2 text-xs font-mono text-white/50">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <Link to="/blog/" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <span className="text-cyan-400 truncate">Free AI Visibility Measurement</span>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/70 hover:text-cyan-400 px-3 py-1.5 rounded-full border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Lab</span>
            </Link>
            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-cyan-400 hover:text-white px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 transition-all cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copied ? "Copied" : "Share"}</span>
            </button>
          </div>
        </div>

        {/* Article Header */}
        <header className="space-y-6 pb-8 border-b border-white/10">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider">
              AEO/GEO , AI Visibility
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider">
              Zero-Cost Measurement Stack
            </span>
          </div>

          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.15]">
            How to Measure AI Search Visibility for Free (Without Paid Tools)
          </h1>

          <p className="text-base sm:text-lg text-white/70 leading-relaxed font-normal">
            If you have the budget to purchase AI visibility tools like Profound or Scrunch AI, this article may not be for you. This guide is for SEO professionals and business owners who want to measure AI search performance without paying for premium tools.
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 text-xs font-mono text-white/50">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-white/80 font-bold">Kanna Narendra Kumar</span>
              <span>&bull;</span>
              <span className="text-cyan-400">Senior SEO Specialist &amp; AI Search Strategist</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                <span>Oct 14, 2026</span>
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                <span>8 min read</span>
              </span>
            </div>
          </div>
        </header>

        {/* Key Takeaways Box — High Information Gain Container for AI Bots & Readers */}
        <section
          aria-label="Key Takeaways"
          className="my-7 p-5 sm:p-6 rounded-xl bg-[#0c1626] border-2 border-cyan-400 shadow-lg shadow-cyan-950/20"
        >
          <div className="flex items-center gap-2 mb-3.5 text-cyan-400">
            <Sparkles className="w-4 h-4 shrink-0 text-cyan-400" />
            <h2 className="text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-cyan-400 m-0">
              Key Takeaways
            </h2>
          </div>

          <ul className="list-disc pl-5 space-y-2.5 text-sm sm:text-[15px] text-slate-200 leading-relaxed marker:text-cyan-400">
            <li>
              <strong className="text-white">Free 5-Data-Source Stack:</strong> You do not need expensive enterprise tools like Profound or Scrunch AI. You can measure full-funnel AI search performance using Google Search Console, Bing Webmaster Tools, Server Log Files, GA4, and Microsoft Clarity.
            </li>
            <li>
              <strong className="text-white">Prioritize High-Intent URLs:</strong> Do not overwhelm your analysis by tracking every site URL. Focus your weekly reporting on core product/service pages, comparison guides, high-intent landing pages, and business-critical blogs.
            </li>
            <li>
              <strong className="text-white">Crawl ≠ Citation ≠ Visit:</strong> A bot request in server logs confirms an AI crawler accessed the page; it does not mean your content was cited in an answer or clicked by a user.
            </li>
            <li>
              <strong className="text-white">Separate Search Bots from Training Bots:</strong> When auditing server logs, isolate search/retrieval crawlers (e.g. <code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1 py-0.5 rounded text-xs font-mono">OAI-SearchBot</code>) from training crawlers (<code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1 py-0.5 rounded text-xs font-mono">GPTBot</code>) and user fetches (<code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1 py-0.5 rounded text-xs font-mono">ChatGPT-User</code>).
            </li>
            <li>
              <strong className="text-white">Never Combine GSC Impressions &amp; Bing Citations:</strong> Google AI impressions and Bing Copilot citations measure fundamentally different visibility states. Keep them separate in your spreadsheet instead of combining them into a misleading single score.
            </li>
            <li>
              <strong className="text-white">The 4-Stage AI Search Signal Chain:</strong> Evaluate performance sequentially: <span className="font-mono text-cyan-300 font-semibold">Crawled (Logs) &rarr; Visible or Cited (GSC / Bing) &rarr; Clicked (GA4) &rarr; Visited (Clarity)</span>.
            </li>
            <li>
              <strong className="text-white">Track Performance for 90 Days:</strong> Measure weekly metrics over approximately three months to establish meaningful baselines and validate whether content and technical updates drive genuine AI search gains.
            </li>
          </ul>
        </section>

        {/* Article Body — 100% Original Content Preserved Word-for-Word */}
        <article className="py-10 space-y-8 text-white/85 text-base sm:text-lg leading-relaxed font-sans">
          
          <div className="space-y-4">
            <p>
              If you have the budget to purchase AI visibility tools like Profound or Scrunch AI, this article may not be for you.
            </p>

            <p>
              This guide is for SEO professionals and business owners who want to measure AI search performance without paying for premium tools.
            </p>

            <p>You can start with five data sources:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/90">
              <li>Google Search Console — AI Performance</li>
              <li>Bing Webmaster Tools — AI Performance</li>
              <li>Server log files</li>
              <li>Google Analytics 4 (GA4)</li>
              <li>Microsoft Clarity</li>
            </ul>

            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-bold text-lg sm:text-xl shadow-xl my-6 not-italic">
              The goal is simple: understand whether AI bots are accessing your pages, whether your content is appearing in AI search, and whether visitors are coming to your website from AI platforms.
            </blockquote>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Step 1 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">1</span>
              <span>Step 1: Select Your Important Pages</span>
            </h2>

            <p>Don't track every URL on your website. It creates too much data and makes analysis difficult.</p>

            <p>Start with your most important pages:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/90">
              <li>Product and service pages</li>
              <li>Comparison pages</li>
              <li>High-intent landing pages</li>
              <li>Blogs that target important business questions</li>
            </ul>

            <p className="font-semibold text-white">
              Use the same list of URLs throughout your analysis.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 2 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">2</span>
              <span>Step 2: Collect Google Search Console Data</span>
            </h2>

            <p>
              Open Google Search Console and look for the Generative AI performance report, if available for your website.
            </p>

            <p>
              Export the available data for your selected period and record AI impressions by page and week.
            </p>

            <p>
              This helps you understand how your pages perform in Google's supported AI search experiences.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 3 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">3</span>
              <span>Step 3: Collect Bing Webmaster Tools Data</span>
            </h2>

            <p>
              Open Bing Webmaster Tools and access the AI Performance report.
            </p>

            <p>
              Export the available citation data and organize it by page and week.
            </p>

            <p>
              This helps you understand which pages are cited in supported AI experiences, including Microsoft Copilot.
            </p>

            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm font-medium">
              <strong>Note:</strong> Google AI impressions and Bing citations are different metrics. Keep them separate.
            </div>
          </section>

          {/* Mid-Article AI Visibility Audit CTA Callout (~30% Scroll) */}
          <div className="my-8 p-6 sm:p-7 rounded-2xl bg-gradient-to-r from-[#0c1629] via-[#0f1d38] to-[#0c1426] border border-cyan-500/35 shadow-xl shadow-cyan-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 not-prose">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>AI Search Readiness Check</span>
              </div>
              <h3 className="font-heading font-bold text-lg sm:text-xl text-white leading-snug">
                Need Help Diagnosing Low AI Citations or Crawler Blocks?
              </h3>
              <p className="text-xs sm:text-sm text-white/70 max-w-xl leading-relaxed">
                Get a complete 4-layer AI Visibility Audit covering server log bot analysis, zero-JS HTML rendering, and customer fan-out prompt opportunities.
              </p>
            </div>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
            >
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Step 4 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">4</span>
              <span>Step 4: Analyze Server Log Files</span>
            </h2>

            <p>Download your server or CDN logs for the same period.</p>

            <p>
              Identify relevant AI search and retrieval bots, then group their requests by page and week.
            </p>

            <p>For example, OpenAI uses different crawlers for different purposes:</p>

            <ul className="list-disc pl-6 space-y-2 text-white/90">
              <li>
                <Link
                  to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
                  className="text-cyan-400 underline underline-offset-4 hover:text-cyan-300 font-semibold"
                >
                  OAI-SearchBot
                </Link>{" "}
                helps discover content for{" "}
                <Link
                  to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
                  className="text-cyan-400 underline underline-offset-4 hover:text-cyan-300 font-semibold"
                >
                  ChatGPT search
                </Link>
                .
              </li>
              <li>
                <strong>GPTBot</strong> is associated with crawling for potential model training.
              </li>
              <li>
                <strong>ChatGPT-User</strong> is used for certain user-triggered fetches.
              </li>
            </ul>

            <p>
              If your goal is to measure AI search activity, focus on the{" "}
              <Link
                to="/blog/can-ai-search-engines-crawl-understand-cite-website/"
                className="text-cyan-400 underline underline-offset-4 hover:text-cyan-300 font-semibold"
              >
                relevant search and retrieval bots
              </Link>
              . Keep training-related crawling and user-triggered fetches separate.
            </p>

            <p>
              Also, separate successful page responses from failed requests and remove irrelevant URLs.
            </p>

            <p>
              Your output should show how many times relevant AI crawlers requested each important page during each week.
            </p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-200 text-base font-semibold not-italic">
              Remember: A bot request means that a page was fetched. It does not mean the page was cited or shown to a user.
            </blockquote>

            {/* Contextual Link Card to Previous Blogs */}
            <div className="my-5 p-4 rounded-2xl bg-[#0a101f] border border-cyan-500/25 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider font-bold">
                  Recommended Reading &bull; Crawler Verification
                </span>
                <p className="text-sm font-heading font-bold text-white">
                  Want the full breakdown of AI bot behavior &amp; server log status codes?
                </p>
                <p className="text-xs text-white/60">
                  Read the complete technical checklist for OAI-SearchBot, ClaudeBot, and Google-Extended.
                </p>
              </div>
              <Link
                to="/blog/can-ai-search-engines-crawl-understand-cite-website/"
                className="shrink-0 px-4 py-2 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-xs font-mono text-cyan-300 hover:text-white transition-colors flex items-center gap-1.5"
              >
                <span>Read Crawler Guide</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 5 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">5</span>
              <span>Step 5: Collect AI Referral Traffic From GA4</span>
            </h2>

            <p>
              Open GA4 and go to <strong>Reports &rarr; Acquisition &rarr; Traffic acquisition</strong>.
            </p>

            <p>
              Identify traffic from AI platforms such as ChatGPT and Claude, along with other sources where referral information is available.
            </p>

            <p>Record the following by landing page and week:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/90">
              <li>Sessions</li>
              <li>Engaged sessions</li>
              <li>Conversions</li>
            </ul>

            <p>
              This helps you understand whether AI platforms are sending visitors to your website.
            </p>

            <p className="text-sm text-white/70 italic">
              Some AI-assisted visits may appear as direct traffic, so GA4 will not capture every visit originating from an AI interaction.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 6 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">6</span>
              <span>Step 6: Use Microsoft Clarity to Understand Visitor Behaviour</span>
            </h2>

            <p>
              Use Microsoft Clarity to analyze visitors who arrive through identifiable AI referrals.
            </p>

            <p>
              Check which pages they visit, how far they scroll and whether they interact with important elements such as enquiry forms.
            </p>

            <p>
              This helps you understand what visitors do after reaching your website.
            </p>

            <p className="font-semibold text-white">
              Clarity measures visitor behaviour, not AI citations.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 7 */}
          <section className="space-y-5">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">7</span>
              <span>Step 7: Combine the Data in One Spreadsheet</span>
            </h2>

            <p>
              Choose a fixed reporting period, such as three months, and divide it into weekly intervals.
            </p>

            <p>Create a spreadsheet with these columns:</p>

            <ul className="list-disc pl-6 space-y-1 text-white/90 font-mono text-sm">
              <li>Page URL</li>
              <li>Week</li>
              <li>AI bot requests</li>
              <li>Google AI impressions</li>
              <li>Bing citations</li>
              <li>AI referral sessions</li>
            </ul>

            {/* Interactive Styled Spreadsheet Table Preview */}
            <div className="my-6 rounded-2xl border border-white/15 bg-[#090e1c] overflow-hidden shadow-2xl">
              <div className="px-5 py-3.5 bg-[#0d1428] border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-cyan-300">
                    Sample Tracking Spreadsheet Structure
                  </span>
                </div>
                <span className="text-[10px] font-mono text-white/40">3-Month Weekly Intervals</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-white/[0.04] text-white/80 font-mono uppercase text-[11px] border-b border-white/10">
                      <th className="py-3 px-4">Page URL</th>
                      <th className="py-3 px-4">Week</th>
                      <th className="py-3 px-4 text-right">AI bot requests</th>
                      <th className="py-3 px-4 text-right">Google AI impressions</th>
                      <th className="py-3 px-4 text-right">Bing citations</th>
                      <th className="py-3 px-4 text-right">AI referral sessions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06] font-mono text-white/90">
                    <tr className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 font-bold text-cyan-300">/seo-services/</td>
                      <td className="py-3 px-4 text-white/60">Week 1</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-200">35</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-200">120</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-400">8</td>
                      <td className="py-3 px-4 text-right font-semibold text-indigo-300">12</td>
                    </tr>
                    <tr className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 font-bold text-cyan-300">/seo-services/</td>
                      <td className="py-3 px-4 text-white/60">Week 2</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-200">48</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-200">150</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-400">12</td>
                      <td className="py-3 px-4 text-right font-semibold text-indigo-300">18</td>
                    </tr>
                    <tr className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 font-bold text-cyan-300">/seo-audit/</td>
                      <td className="py-3 px-4 text-white/60">Week 1</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-200">5</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-200">40</td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-400">1</td>
                      <td className="py-3 px-4 text-right font-semibold text-indigo-300">3</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-sm text-white/60 italic">
              The figures above are examples, not actual performance data.
            </p>

            <p className="font-semibold text-white">
              Keep the metrics separate. They measure different stages of AI search performance and should not be added together into one score.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 8 */}
          <section className="space-y-6">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">8</span>
              <span>Step 8: Understand What the Data Is Telling You</span>
            </h2>

            <p>
              Before comparing the numbers, you need to understand what each data source means.
            </p>

            <p>Each tool tells you something different about your website.</p>

            <div className="space-y-2.5 my-4">
              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <span className="font-mono font-bold text-cyan-400 sm:w-64 shrink-0">
                  Server log files = Crawled:
                </span>
                <span className="text-white/80">
                  An AI bot visited and requested your page. This does not mean your page was cited.
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <span className="font-mono font-bold text-indigo-400 sm:w-64 shrink-0">
                  Google Search Console AI = Visibility:
                </span>
                <span className="text-white/80">
                  Your page received impressions in Google's reported AI search experiences.
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <span className="font-mono font-bold text-emerald-400 sm:w-64 shrink-0">
                  Bing Webmaster Tools = Citations:
                </span>
                <span className="text-white/80">
                  Your page was cited in the AI experiences covered by Bing's report.
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <span className="font-mono font-bold text-amber-400 sm:w-64 shrink-0">
                  GA4 = Clicks and visits:
                </span>
                <span className="text-white/80">
                  People clicked through from an AI platform and visited your website, where the referral could be identified.
                </span>
              </div>

              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                <span className="font-mono font-bold text-purple-400 sm:w-64 shrink-0">
                  Microsoft Clarity = Visitor behaviour:
                </span>
                <span className="text-white/80">
                  You can see how visitors interact with your website after they arrive.
                </span>
              </div>
            </div>

            <p className="text-white font-bold text-lg pt-2">In simple terms:</p>

            <div className="p-4 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-sm sm:text-base text-cyan-300 font-bold text-center overflow-x-auto shadow-inner">
              Crawled &rarr; Visible or cited &rarr; Clicked &rarr; Visited your website
            </div>

            <p className="font-semibold text-white/90">
              These are different signals. One does not guarantee the next.
            </p>

            {/* Diagnostic Matrix Table */}
            <div className="pt-4 space-y-3">
              <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                <span>How to use these signals</span>
              </h3>

              <div className="rounded-2xl border border-white/15 bg-[#090e1c] overflow-hidden shadow-2xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-[#0e162c] text-white/80 font-mono uppercase text-[11px] border-b border-white/10">
                        <th className="py-3.5 px-4 font-bold text-cyan-300">Crawled?</th>
                        <th className="py-3.5 px-4 font-bold text-indigo-300">Visible or cited?</th>
                        <th className="py-3.5 px-4 font-bold text-white">What does it mean?</th>
                        <th className="py-3.5 px-4 font-bold text-emerald-400">What should you do?</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06] text-white/85">
                      <tr className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-4 font-mono font-bold text-emerald-400">High</td>
                        <td className="py-4 px-4 font-mono font-bold text-emerald-400">High</td>
                        <td className="py-4 px-4 leading-relaxed">
                          AI bots are requesting your pages, and your content is receiving visibility or citations in the reports.
                        </td>
                        <td className="py-4 px-4 font-medium text-emerald-300 leading-relaxed">
                          Maintain the pages, update the content and improve internal links.
                        </td>
                      </tr>

                      <tr className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-4 font-mono font-bold text-emerald-400">High</td>
                        <td className="py-4 px-4 font-mono font-bold text-rose-400">Low</td>
                        <td className="py-4 px-4 leading-relaxed">
                          AI bots are visiting your pages, but reported AI visibility is low.
                        </td>
                        <td className="py-4 px-4 font-medium text-amber-300 leading-relaxed">
                          Review whether your content answers user questions clearly and offers useful, original information.
                        </td>
                      </tr>

                      <tr className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-4 font-mono font-bold text-rose-400">Low</td>
                        <td className="py-4 px-4 font-mono font-bold text-emerald-400">High</td>
                        <td className="py-4 px-4 leading-relaxed">
                          Your pages are receiving reported visibility despite few recent bot requests in your logs.
                        </td>
                        <td className="py-4 px-4 font-medium text-cyan-300 leading-relaxed">
                          Check the reporting dates, crawler coverage and whether cached content or earlier crawling explains the result.
                        </td>
                      </tr>

                      <tr className="hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-4 font-mono font-bold text-rose-400">Low</td>
                        <td className="py-4 px-4 font-mono font-bold text-rose-400">Low</td>
                        <td className="py-4 px-4 leading-relaxed">
                          You have little recorded crawler activity and little reported AI visibility.
                        </td>
                        <td className="py-4 px-4 font-medium text-rose-300 leading-relaxed">
                          Check robots.txt, page access, rendering, indexability and internal links.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <p className="text-sm text-white/70 italic">
              High and low are relative to your own website and the period you are measuring. There is no universal number that defines high AI visibility.
            </p>

            <div className="space-y-3 pt-2">
              <p>
                <strong>Important:</strong> Google AI impressions and Bing citations are not the same metric. Keep them separate in your spreadsheet. Use each report to understand the AI visibility it actually measures.
              </p>

              <p>
                Also, do not assume that more bot requests automatically mean more citations. The purpose of this analysis is to identify patterns and understand which pages need improvement.
              </p>
            </div>

            {/* Contextual Link Card to Why Rank on Google Not ChatGPT */}
            <div className="my-5 p-4 rounded-2xl bg-[#0a101f] border border-indigo-500/25 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-mono text-indigo-400 uppercase tracking-wider font-bold">
                  Recommended Reading &bull; Strategic AI SEO
                </span>
                <p className="text-sm font-heading font-bold text-white">
                  Why do competitors appear in ChatGPT while you only rank on Google?
                </p>
                <p className="text-xs text-white/60">
                  Explore fan-out queries, JavaScript rendering friction, and the 6-step AI visibility audit protocol.
                </p>
              </div>
              <Link
                to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
                className="shrink-0 px-4 py-2 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-xs font-mono text-indigo-300 hover:text-white transition-colors flex items-center gap-1.5"
              >
                <span>Read Strategy Guide</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Contextual Link Card to Keyword Research for AI Visibility */}
            <div className="my-5 p-4 rounded-2xl bg-[#0a101f] border border-cyan-500/25 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <span className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider font-bold">
                  Recommended Reading &bull; Customer Intent &amp; Question Memory
                </span>
                <p className="text-sm font-heading font-bold text-white">
                  Want to find the exact buying questions that trigger AI answers?
                </p>
                <p className="text-xs text-white/60">
                  Build a Question Memory using Google Search Console, Reddit, customer reviews, and sales conversations.
                </p>
              </div>
              <Link
                to="/blog/how-to-do-keyword-research-for-ai-visibility/"
                className="shrink-0 px-4 py-2 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-xs font-mono text-cyan-300 hover:text-white transition-colors flex items-center gap-1.5"
              >
                <span>Read Intent Guide</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Step 9 */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 flex items-center justify-center text-sm font-mono font-bold">9</span>
              <span>Step 9: Track Performance for Three Months</span>
            </h2>

            <p>Repeat the same process every week for approximately three months.</p>

            <p>Compare changes in:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/90">
              <li>AI bot activity</li>
              <li>Google AI impressions</li>
              <li>Bing citations</li>
              <li>AI referral sessions</li>
              <li>Conversions from identifiable AI referrals</li>
            </ul>

            <p>
              Record when you make important technical or content changes.
            </p>

            <p>
              If visibility improves consistently after an update, investigate whether the change contributed to the improvement. Other factors, including platform updates and competitor activity, may also affect results.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Final Thoughts */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              Final Thoughts
            </h2>

            <p>
              You don't need premium AI visibility tools to start measuring your website's AI search performance.
            </p>

            <p>
              Use Google Search Console, Bing Webmaster Tools, server logs, GA4 and Microsoft Clarity to understand crawler access, AI visibility and visitor behaviour.
            </p>

            <p>
              The goal is to connect these signals and identify which pages need attention.
            </p>

          </section>

        </article>

        {/* Interactive 9-Step Tracking Checklist Box */}
        <section className="my-10 p-6 sm:p-8 rounded-3xl bg-[#090e1c] border border-cyan-500/30 shadow-2xl">
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-white/10">
              <div>
                <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest">
                  Interactive Implementation Tracker
                </span>
                <h3 className="font-heading font-black text-xl sm:text-2xl text-white tracking-tight mt-1">
                  Your Free AI Visibility Tracking Checklist
                </h3>
              </div>

              <div className="text-left sm:text-right shrink-0">
                <span className="text-xs font-mono text-cyan-400 font-bold">
                  {completedStepsCount} of {stepsTrackingList.length} Complete
                </span>
                <div className="w-36 h-2 rounded-full bg-white/10 mt-1.5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-all duration-300"
                    style={{ width: `${(completedStepsCount / stepsTrackingList.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
              {stepsTrackingList.map((st) => {
                const isChecked = !!checkedSteps[st.id];
                return (
                  <div
                    key={st.id}
                    onClick={() => toggleStep(st.id)}
                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer select-none text-left flex items-start gap-3 ${
                      isChecked
                        ? "bg-cyan-950/30 border-cyan-500/50 text-white shadow-lg shadow-cyan-950/30"
                        : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20 hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="mt-0.5 shrink-0 text-cyan-400">
                      {isChecked ? (
                        <CheckSquare className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono text-cyan-400 font-bold">
                          {st.stepNum}
                        </span>
                        <p className={`font-heading font-bold text-xs ${isChecked ? "text-cyan-300" : "text-white"}`}>
                          {st.title}
                        </p>
                      </div>
                      <p className="text-[11px] text-white/60 leading-relaxed font-normal">
                        {st.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs sm:text-sm text-white/75">
                Want a done-for-you 4-layer AI Visibility Audit across server logs, GSC, Bing citations, and ChatGPT prompts?
              </p>
              <Link
                to="/ai-visibility-audit/"
                className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-all"
              >
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Book Your AI Visibility Audit</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Related Technical Playbooks & Previous Blogs (Direct Cross-Linking) */}
        <section className="my-12 space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              <span>Related Technical Playbooks &amp; Case Studies</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/blog/how-to-do-keyword-research-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase">
                  Intent &amp; AI Visibility
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-cyan-400 transition-colors leading-snug">
                  How to Do Keyword Research for AI Visibility
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Customer-intent-first approach using GSC, Reddit, customer reviews, and sales conversations to build a Question Memory.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-cyan-400 font-semibold">
                <span>Read Intent Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 uppercase">
                  AEO / GEO Strategy
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-indigo-400 transition-colors leading-snug">
                  Why Do I Rank on Google but Don't Appear in ChatGPT?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Why Google rankings don't equal ChatGPT citations, how fan-out queries operate, and the 6-step AI visibility audit protocol.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-indigo-400 font-semibold">
                <span>Read Strategy Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/can-ai-search-engines-crawl-understand-cite-website/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-purple-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-500/10 text-purple-300 border border-purple-500/20 uppercase">
                  Technical SEO &amp; AEO
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-purple-400 transition-colors leading-snug">
                  Can AI Search Engines Crawl, Understand and Cite Your Website?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  The complete 4-layer AI visibility framework and technical checklist for OAI-SearchBot, ClaudeBot, and Google-Extended.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-purple-400 font-semibold">
                <span>Read Technical Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/how-to-audit-a-website-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-emerald-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 uppercase">
                  10-Point Technical Checklist
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-emerald-400 transition-colors leading-snug">
                  How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Audit your website yourself with 10 practical checks covering bot crawling, indexability, JavaScript rendering parity, and citations.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <span>Read Audit Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>

        {/* Author Bio Card */}
        <footer className="mt-12 pt-8 border-t border-white/10">
          <div className="p-6 sm:p-7 rounded-3xl bg-[#0a0f1d] border border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left shadow-2xl">
            <img
              src="/narendra-workstation.jpg"
              alt="Kanna Narendra Kumar"
              className="w-24 h-24 rounded-2xl object-cover object-[center_20%] border border-cyan-500/40 shrink-0 shadow-xl"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/narendra-avatar.jpg";
              }}
            />
            <div className="space-y-2.5 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="font-heading font-bold text-lg text-white">
                    Written by Kanna Narendra Kumar
                  </h4>
                  <p className="text-xs font-mono text-cyan-400">
                    Senior SEO Specialist &amp; AI Search Strategist
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/narendra-kumar-kanna/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-xs text-white/80 font-mono transition-colors self-center sm:self-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>LinkedIn Profile</span>
                </a>
              </div>
              <p className="text-xs sm:text-sm text-white/75 leading-relaxed font-sans">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic visibility and high-intent inbound pipeline for global B2B SaaS. Pioneer of the Hybrid Engine Optimization (HEO) framework unifying traditional search with generative AI platforms. Available globally for strategic advisory and high-impact enterprise execution.
              </p>
            </div>
          </div>
        </footer>

        {/* Collaboration CTA Footer */}
        <section className="mt-10 p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Zap className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
            Need High-Impact Search &amp; AI Execution?
          </h3>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Whether you are navigating Google AI Overviews, preparing a CMS migration, or scaling high-converting organic SQLs, let&apos;s connect directly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai-visibility-audit/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Schedule Strategy Discussion</span>
              <ArrowUpRight className="w-4 h-4 text-cyan-400" />
            </Link>
          </div>
        </section>

      </main>
    </div>
  );
}
